import type { ProfileConfig } from "@worker/config-schema";
import { el, prefersReducedMotion } from "../lib/dom";

/**
 * Cycling typewriter: types each line, pauses, deletes, moves to the next.
 * If only one line is provided, it types once and stays (no cycling).
 * If `prefers-reduced-motion` is active and the motion config permits it,
 * we render statically instead of animating.
 */
export function mountTypewriter(
  desc: ProfileConfig["profile"]["description"],
  motion: ProfileConfig["motion"],
): HTMLElement {
  const node = el("p", { class: "description" });
  const text = el("span", { class: "description__text" });
  const caret = el("span", {
    class: "description__caret",
    "aria-hidden": "true",
  }, ["|"]);
  node.append(text, caret);

  const lines = desc.lines;
  if (lines.length === 0) return node;

  const shouldReduce =
    motion.respectReducedMotion &&
    motion.reducedMotionSuppresses.includes("typewriter") &&
    prefersReducedMotion();

  if (!desc.typewriter.enabled || shouldReduce) {
    text.textContent = lines[0] ?? "";
    caret.remove();
    return node;
  }

  const speedMul = motion.speedOverrides["typewriter"] ?? 1;
  if (speedMul === 0) {
    text.textContent = lines[0] ?? "";
    caret.remove();
    return node;
  }

  const type = desc.typewriter.typeSpeedMs / speedMul;
  const del = desc.typewriter.deleteSpeedMs / speedMul;
  const pause = desc.typewriter.pauseMs / speedMul;

  let lineIndex = 0;
  let charIndex = 0;
  let mode: "typing" | "pausing" | "deleting" = "typing";
  let timer: number | undefined;

  function tick() {
    const line = lines[lineIndex] ?? "";
    if (mode === "typing") {
      charIndex += 1;
      text.textContent = line.slice(0, charIndex);
      if (charIndex >= line.length) {
        mode = "pausing";
        timer = window.setTimeout(() => {
          mode = lines.length > 1 ? "deleting" : "pausing";
          if (mode === "deleting") tick();
        }, pause);
        return;
      }
      timer = window.setTimeout(tick, type);
    } else if (mode === "deleting") {
      charIndex -= 1;
      text.textContent = line.slice(0, Math.max(0, charIndex));
      if (charIndex <= 0) {
        lineIndex = (lineIndex + 1) % lines.length;
        charIndex = 0;
        mode = "typing";
      }
      timer = window.setTimeout(tick, del);
    }
  }

  // Start on next frame so the caret is visible before typing begins.
  requestAnimationFrame(() => {
    timer = window.setTimeout(tick, type);
  });

  // Allow the caller to clean up (e.g. when admin preview swaps config).
  node.addEventListener("bio:teardown", () => {
    if (timer) clearTimeout(timer);
  });

  return node;
}
